import React, { useState } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { getPackageName } from '@src/services/package';
import { delimitNumber } from '@src/utils/number';
import pluralize from '@src/utils/pluralize';
import NotificationDialogV2 from '@src/components/NotificationDialogV2';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import PackageStatusBadge from './PackageStatusBadge';
import PackageInfoRow from './PackageInfoRow';
import AutoRenewSectionNew from './AutoRenewSectionNew';
import V2WarningBox from './V2WarningBox';
import STTDuration from './STTDuration';
import {
  StyledPackageCardNew,
  StyledExpiryDate,
  StyledNextResetText,
  StyledSTTLabel,
} from './index.style';
import PackageInfoAPIRow from './PackageInfoAPIRow';

const DATE_FORMAT = 'DD/MM/YYYY - HH:mm';

const PackageCard = ({
  packageInfo = {},
  autoRenewalPlan = {},
  usingOrder = {},
  statusType = 'current',
  isExpired = false,
  isMobile = false,
  // Character data
  remainingCredits = 0,
  totalCredits = 0,
  apiLimitRemaining = 0,
  apiLimitTotal = 0,
  // Features
  professionalVoices,
  instantVoices,
  concurrentDevices,
  concurrentRequest,
  sttDurationSeconds,
  // Auto-renewal
  hasAutoRenew = false,
  subscriptionPurchase,
  paymentMethodType,
  orderInfo,
  isInAppPurchase,
  // V2 warning
  showV2Warning = false,
  // Package type flags
  isFreePackage = false,
  isBusinessPackage = false,
  isV3Package = false,
  isCustomPackage = false,
  // Next reset info
  nextResetAt,
  creditsPerCycle,
  showNextResetCycle,
  // Tooltip data
  purchasedTopUpCredits = 0,
  totalCapturedRolloverCredits = 0,
  capturedReferralCreditsCycle = 0,
  capturedReferralCreditsCustom = 0,
  isTwoPackage,
}) => {
  const { i18n } = useTranslation();
  const { t, language } = i18n;
  const { packageExpiryDate } = usingOrder;
  const [showInfoCredits, setShowInfoCredits] = useState(false);
  const [showInfoApiLimit, setShowInfoApiLimit] = useState(false);

  const formatExpiryDate = () => {
    if (!packageExpiryDate)
      return isFreePackage || isBusinessPackage ? t('endless') : '';
    try {
      const date = moment(packageExpiryDate);
      if (!date.isValid()) return '';
      return date.format(DATE_FORMAT);
    } catch (error) {
      return '';
    }
  };

  const formatCharacterValue = () => {
    // V3 Sub and Free: remaining/total with tooltip
    if (isV3Package && totalCredits > 0) {
      return `${delimitNumber(remainingCredits, language)}/${delimitNumber(
        totalCredits,
        language,
      )} ${isMobile ? '' : pluralize(totalCredits, t('credit'), language)}`;
    }

    // Custom V3+ and V2: remaining only, no tooltip
    return `${delimitNumber(remainingCredits, language)} ${
      isMobile ? '' : pluralize(remainingCredits, t('credit'), language)
    }`;
  };

  const getCharacterTooltipContent = () => {
    // Only show tooltip for V3 or Free packages with totalCredits > 0
    if (!(isV3Package || isFreePackage) || totalCredits <= 0) {
      return undefined;
    }

    const hasTopUpCredits = Boolean(purchasedTopUpCredits);
    const hasReferralCredits = Boolean(
      capturedReferralCreditsCycle || capturedReferralCreditsCustom,
    );
    const hasCaptureRolloverCredits = Boolean(totalCapturedRolloverCredits);

    // Check if tooltip should be shown
    const shouldShowTooltip =
      showNextResetCycle ||
      hasTopUpCredits ||
      hasAutoRenew ||
      hasCaptureRolloverCredits;

    if (!shouldShowTooltip) {
      return undefined;
    }

    // Build text for topup, referral, and rollover credits
    const getTextTopupReferralAndRollover = () => {
      if (!hasReferralCredits && !hasTopUpCredits && !hasCaptureRolloverCredits)
        return '';

      const listNoteCredits = [];

      if (hasReferralCredits) {
        listNoteCredits.push(
          t('rewardCharacters', {
            capturedReferralCreditsCycle: delimitNumber(
              isCustomPackage
                ? capturedReferralCreditsCustom
                : capturedReferralCreditsCycle,
              language,
            ),
          }),
        );
      }

      if (hasTopUpCredits) {
        listNoteCredits.push(
          t('additionalPurchasedCharacters', {
            purchasedTopUpCredits: delimitNumber(
              purchasedTopUpCredits,
              language,
            ),
          }),
        );
      }

      if (hasCaptureRolloverCredits) {
        listNoteCredits.push(
          t('rolloverCharacters', {
            rolloverCredits: delimitNumber(
              totalCapturedRolloverCredits,
              language,
            ),
          }),
        );
      }

      const note = listNoteCredits.join('; ');
      return `${t('includes')} ${note}.`;
    };

    const addOnCreditsNote = getTextTopupReferralAndRollover();
    if (!addOnCreditsNote) return null;

    // Return tooltip content as JSX
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          textAlign: 'left',
          gap: '8px',
        }}
      >
        {addOnCreditsNote && (
          <Typography sx={{ fontSize: '14px' }}>{addOnCreditsNote}</Typography>
        )}
      </Box>
    );
  };

  const shouldShowApiLimit = () =>
    (isV3Package || (isCustomPackage && apiLimitRemaining > 0)) &&
    apiLimitRemaining !== undefined;

  const formattedCharacterValue = formatCharacterValue();
  const characterTooltipContent = getCharacterTooltipContent();
  const showCharacterTooltip = Boolean(characterTooltipContent);
  const showApiLimit = shouldShowApiLimit();

  // Get text for mobile popup
  const getTextTopupReferralAndRollover = () => {
    const hasTopUpCredits = Boolean(purchasedTopUpCredits);
    const hasReferralCredits = Boolean(
      capturedReferralCreditsCycle || capturedReferralCreditsCustom,
    );
    const hasCaptureRolloverCredits = Boolean(totalCapturedRolloverCredits);

    if (!hasReferralCredits && !hasTopUpCredits && !hasCaptureRolloverCredits)
      return '';

    const listNoteCredits = [];

    if (hasReferralCredits) {
      listNoteCredits.push(
        t('rewardCharacters', {
          capturedReferralCreditsCycle: delimitNumber(
            isCustomPackage
              ? capturedReferralCreditsCustom
              : capturedReferralCreditsCycle,
            language,
          ),
        }),
      );
    }

    if (hasTopUpCredits) {
      listNoteCredits.push(
        t('additionalPurchasedCharacters', {
          purchasedTopUpCredits: delimitNumber(purchasedTopUpCredits, language),
        }),
      );
    }

    if (hasCaptureRolloverCredits) {
      listNoteCredits.push(
        t('rolloverCharacters', {
          rolloverCredits: delimitNumber(
            totalCapturedRolloverCredits,
            language,
          ),
        }),
      );
    }

    const note = listNoteCredits.join('; ');
    return `${t('includes')} ${note}.`;
  };

  const handleClickShowInfoTopupAndRollover = () => {
    if (isMobile) setShowInfoCredits(true);
    return null;
  };
  const handleClickShowInfoApiLimit = () => {
    if (isMobile) setShowInfoApiLimit(true);
    return null;
  };

  const renderButtonGotIt = () => (
    <CommonButton
      variant="contained"
      customStyle={{
        width: '100%',
        height: '40px',
        borderRadius: '8px',
        margin: 'auto 8px',
      }}
      textProps={{
        variantText: 'body-14-semibold',
        color: COLOR.darkBlue,
      }}
      onClick={() => {
        setShowInfoCredits(false);
        setShowInfoApiLimit(false);
      }}
    >
      {t('gotIt')}
    </CommonButton>
  );

  return (
    <StyledPackageCardNew isMobile={isMobile}>
      <Box className="header-container-package">
        <Box className="header-left">
          <Typography className="package-title">
            {getPackageName(
              packageInfo?.name,
              packageInfo?.code,
              language,
              t,
              true,
            )}
          </Typography>

          <PackageStatusBadge
            type={statusType}
            isExpired={isExpired}
            isTwoPackage={isTwoPackage}
          />
        </Box>
        <Box className="expiry-container">
          <Typography className="expiry-label">{t('exp')}: </Typography>
          <StyledExpiryDate isExpired={isExpired}>
            {formatExpiryDate()}
          </StyledExpiryDate>
        </Box>
      </Box>

      {!isExpired && <Divider className="divider" />}

      {!isExpired && (
        <Box className="content-container">
          {/* Character count */}
          <Box className="character-section">
            <PackageInfoRow
              label={t('remainingCharactersV2')}
              value={formattedCharacterValue}
              showTooltip={showCharacterTooltip}
              tooltipContent={characterTooltipContent}
              isBold
              isMobile={isMobile}
              onTooltipClick={handleClickShowInfoTopupAndRollover}
            />
            {showApiLimit && (
              <PackageInfoAPIRow
                label={t('apiLimitRemaining')}
                value={
                  apiLimitTotal > 0
                    ? `${delimitNumber(
                        apiLimitRemaining,
                        language,
                      )}/${delimitNumber(apiLimitTotal, language)} ${
                        isMobile
                          ? ''
                          : pluralize(apiLimitTotal, t('credit'), language)
                      }`
                    : `${delimitNumber(apiLimitRemaining, language)} ${
                        isMobile
                          ? ''
                          : pluralize(apiLimitRemaining, t('credit'), language)
                      }`
                }
                isMobile={isMobile}
                showTooltip={!isCustomPackage}
                tooltipContent={
                  <Typography sx={{ fontSize: '14px' }}>
                    {t('apiLimitTooltip')}
                  </Typography>
                }
                onTooltipClick={handleClickShowInfoApiLimit}
              />
            )}
          </Box>

          {/* Rollover info */}
          {showNextResetCycle && (
            <StyledNextResetText isMobile={isMobile}>
              {t('nextResetCycle', {
                creditsPerCycle: delimitNumber(
                  isFreePackage ? remainingCredits : creditsPerCycle,
                  language,
                ),
                formattedNextResetAt: moment(nextResetAt)
                  .add(1, 'days')
                  .format('DD/MM/YYYY'),
              })}
            </StyledNextResetText>
          )}

          {/* Features */}
          {professionalVoices !== undefined && !isFreePackage && (
            <PackageInfoRow
              label={t('voiceCloningProfessional')}
              value={String(professionalVoices)}
              isBold
              isMobile={isMobile}
            />
          )}
          {instantVoices !== undefined && (
            <PackageInfoRow
              label={t('voiceCloningInstant')}
              value={String(instantVoices)}
              isBold
              isMobile={isMobile}
            />
          )}
          {concurrentDevices !== undefined && (
            <PackageInfoRow
              label={t('concurrentDevices')}
              value={String(concurrentDevices)}
              isBold
              isMobile={isMobile}
            />
          )}
          {concurrentRequest !== undefined && !isFreePackage && (
            <PackageInfoRow
              label={t('concurrentApiThreads')}
              value={String(concurrentRequest)}
              isBold
              isMobile={isMobile}
            />
          )}
          {sttDurationSeconds !== undefined &&
            sttDurationSeconds > 0 &&
            !isFreePackage && (
              <Box className="stt-section">
                <StyledSTTLabel isMobile={isMobile}>
                  {t('sttDurationRemaining')}
                </StyledSTTLabel>
                <STTDuration
                  remainingSeconds={sttDurationSeconds}
                  isMobile={isMobile}
                />
              </Box>
            )}

          {/* Auto-renewal section */}
          {hasAutoRenew && (
            <AutoRenewSectionNew
              packageInfo={autoRenewalPlan}
              packageExpiryDate={packageExpiryDate}
              subscriptionPurchase={subscriptionPurchase}
              paymentMethodType={paymentMethodType}
              isMobile={isMobile}
              orderInfo={orderInfo}
              isInAppPurchase={isInAppPurchase}
            />
          )}

          {/* V2 Warning */}
          {showV2Warning && <V2WarningBox />}
        </Box>
      )}
      <NotificationDialogV2
        showIcon={false}
        title={t('detailInfo')}
        description={t('creditNoteOnMobileWeb', {
          addOnCreditsNote: getTextTopupReferralAndRollover(),
        })}
        open={showInfoCredits}
        onClose={() => setShowInfoCredits(false)}
        actionComponent={renderButtonGotIt()}
      />
      <NotificationDialogV2
        showIcon={false}
        title={t('detailInfo')}
        description={t('apiLimitTooltip')}
        open={showInfoApiLimit}
        onClose={() => setShowInfoApiLimit(false)}
        actionComponent={renderButtonGotIt()}
      />
    </StyledPackageCardNew>
  );
};

export default PackageCard;
